import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Copy, Share2, Check, AlertCircle } from 'lucide-react';
import Modal from './Modal';
import { api } from '../../lib/api';
import type { QrStyle } from '../../lib/types';
import { renderQr, canvasToPngBlob, loadLogo, type RenderAssets } from '../../lib/qr/renderQr';
import { PLATFORM_LOGO_URL } from '../../lib/qr/brand';
import { panelMeta } from '../../lib/panelMeta';

interface Props {
  value: string;
  label: string;
  module: string;
  refId?: string;
  /** Nombre base del archivo al guardar (sin extensión). */
  fileName?: string;
  /** Estilo inicial (al reabrir un QR ya guardado). Por defecto se estrena de marca. */
  initialStyle?: QrStyle;
  onClose: () => void;
}

const SHAPE_OPTIONS: { value: QrStyle['shape']; label: string }[] = [
  { value: 'square', label: 'Cuadrado' },
  { value: 'dots', label: 'Puntos' },
];
const COLOR_OPTIONS: { value: QrStyle['color']; label: string }[] = [
  { value: 'black', label: 'Negro' },
  { value: 'accent', label: 'Tu color' },
  { value: 'gradient', label: 'Degradado' },
];

function Segmented<T extends string>({
  value, options, onChange,
}: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="inline-flex rounded-md border border-ui-border bg-ui-bg p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`rounded px-3 py-1.5 text-sm transition-colors ${
            value === opt.value
              ? 'bg-ui-accent text-ui-bg font-medium'
              : 'text-ui-text-muted hover:text-ui-text'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function safeFileName(name: string): string {
  return (name || 'codigo-qr').toLowerCase().normalize('NFD')
    .replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 60) || 'codigo-qr';
}

export default function QrCodeModal({ value, label, module, refId, fileName, initialStyle, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [accent, setAccent] = useState<string | null>(null);
  const [tenantLogoUrl, setTenantLogoUrl] = useState<string | null>(null);
  const [logoImg, setLogoImg] = useState<HTMLImageElement | null>(null);
  const [style, setStyle] = useState<QrStyle>(initialStyle ?? { shape: 'dots', color: 'accent', logoSource: 'none' });
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const saveTimer = useRef<number | null>(null);

  const hasTenantLogo = !!tenantLogoUrl;
  const hasPlatformLogo = !!PLATFORM_LOGO_URL;

  // Carga perezosa de acento + logo del negocio al abrir.
  useEffect(() => {
    let alive = true;
    Promise.allSettled([api.settings.getTheme(), api.settings.get()]).then(([theme, site]) => {
      if (!alive) return;
      if (theme.status === 'fulfilled') setAccent((theme.value?.accent as string) ?? null);
      if (site.status === 'fulfilled') {
        const s = site.value;
        const logo = s?.navLogoUrl ?? s?.footerLogoUrl ?? null;
        setTenantLogoUrl(logo);
        // Por defecto, estrenar con el logo del negocio si existe (look de marca).
        // Si se reabre un QR ya guardado (initialStyle), respetar su estilo.
        if (logo && !initialStyle) setStyle((prev) => ({ ...prev, logoSource: 'tenant' }));
      }
    });
    return () => { alive = false; };
  }, []);

  // Resuelve la URL del logo según la fuente elegida.
  const logoUrl = style.logoSource === 'tenant' ? tenantLogoUrl
    : style.logoSource === 'platform' ? PLATFORM_LOGO_URL
    : null;

  // Carga la imagen del logo (con CORS) cuando cambia la fuente.
  useEffect(() => {
    if (!logoUrl) { setLogoImg(null); return; }
    let alive = true;
    loadLogo(logoUrl).then((img) => { if (alive) setLogoImg(img); });
    return () => { alive = false; };
  }, [logoUrl]);

  // Persiste (registra/sobrescribe) el QR en R2 — un QR canónico por enlace.
  const persist = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await canvasToPngBlob(canvas);
      await api.qr.save({ file: blob, module, refId, label, targetUrl: value, styleJson: JSON.stringify(style) });
    } catch {
      // Silencioso: la persistencia es de respaldo; las acciones funcionan con el blob local.
    }
  }, [module, refId, label, value, style]);

  // Render en vivo + autoguardado con pequeño retraso al cambiar el estilo.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const assets: RenderAssets = { accent, logo: logoImg };
    renderQr(canvas, value, style, assets);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { void persist(); }, 700);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [value, style, accent, logoImg, persist]);

  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 3000);
    return () => window.clearTimeout(t);
  }, [feedback]);

  async function currentBlob(): Promise<Blob | null> {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    try { return await canvasToPngBlob(canvas); } catch { return null; }
  }

  const baseName = safeFileName(fileName ?? label);

  async function handleDownload() {
    const blob = await currentBlob();
    if (!blob) { setFeedback({ kind: 'error', text: 'No se pudo crear la imagen. Inténtalo de nuevo.' }); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setFeedback({ kind: 'ok', text: 'Imagen guardada.' });
  }

  async function handleCopy() {
    const blob = await currentBlob();
    if (!blob) { setFeedback({ kind: 'error', text: 'No se pudo crear la imagen. Inténtalo de nuevo.' }); return; }
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setFeedback({ kind: 'ok', text: 'Imagen copiada. Ya puedes pegarla.' });
    } catch {
      setFeedback({ kind: 'error', text: 'Tu navegador no permitió copiar la imagen. Prueba a guardarla.' });
    }
  }

  async function handleShare() {
    const blob = await currentBlob();
    const file = blob ? new File([blob], `${baseName}.png`, { type: 'image/png' }) : null;
    const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };
    if (file && nav.canShare?.({ files: [file] }) && nav.share) {
      try {
        await nav.share({ files: [file], title: label, text: value });
        return;
      } catch {
        // Cancelado o no disponible: caemos al enlace por WhatsApp.
      }
    }
    const text = encodeURIComponent(`${label ? `${label}\n` : ''}${value}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
  }

  const actionBtn = 'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors';

  return (
    <Modal title="Código QR" onClose={onClose}>
      <div className="space-y-5 pb-5">
        {/* Preview */}
        <div className="flex justify-center">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="rounded-xl bg-white p-4 shadow-lg"
          >
            <canvas ref={canvasRef} className="h-56 w-56 rounded" />
          </motion.div>
        </div>

        <p className="text-center text-sm text-ui-text-subtle break-all">{value}</p>

        {/* Personalización */}
        <div className="space-y-3 rounded-lg border border-ui-border bg-ui-bg/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-ui-text-muted">Forma</span>
            <Segmented value={style.shape} options={SHAPE_OPTIONS} onChange={(v) => setStyle((s) => ({ ...s, shape: v }))} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-ui-text-muted">Color</span>
            <Segmented value={style.color} options={COLOR_OPTIONS} onChange={(v) => setStyle((s) => ({ ...s, color: v }))} />
          </div>
          {(hasTenantLogo || hasPlatformLogo) && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-ui-text-muted">Logo al centro</span>
              <Segmented
                value={style.logoSource}
                options={[
                  { value: 'none' as const, label: 'Sin logo' },
                  ...(hasTenantLogo ? [{ value: 'tenant' as const, label: 'Tu logo' }] : []),
                  ...(hasPlatformLogo ? [{ value: 'platform' as const, label: panelMeta.productName }] : []),
                ]}
                onChange={(v) => setStyle((s) => ({ ...s, logoSource: v }))}
              />
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={handleDownload} className={`${actionBtn} border border-ui-border text-ui-text hover:bg-ui-text/[0.05]`}>
            <Download size={16} /> Guardar
          </button>
          <button type="button" onClick={handleCopy} className={`${actionBtn} border border-ui-border text-ui-text hover:bg-ui-text/[0.05]`}>
            <Copy size={16} /> Copiar
          </button>
          <button type="button" onClick={handleShare} className={`${actionBtn} bg-ui-accent text-ui-bg hover:bg-ui-accent-hover`}>
            <Share2 size={16} /> Compartir
          </button>
        </div>

        <AnimatePresence>
          {feedback && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm ${
                feedback.kind === 'ok'
                  ? 'border border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
                  : 'bg-ui-danger/10 text-ui-danger'
              }`}
            >
              {feedback.kind === 'ok' ? <Check size={16} /> : <AlertCircle size={16} />}
              {feedback.text}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  );
}
